/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * OrganizerSidebar
 * ----------------
 * The organizer rail: Home, Events, Route builder, Participants, Statistics,
 * Settings, with the user chip and Log out pinned to the bottom. Items marked
 * `hidden` stay defined (and routed) but are not shown in the rail.
 *
 * Wide screens get the collapse toggle and the same /arcee/user/logout/ call as
 * SidebarRegular; the difference is the frosted Dawn treatment rather than a
 * solid theme-primary fill. On a phone OrganizerLayout passes `narrow`, and the
 * rail becomes a drawer that only shows when the top bar's menu is tapped.
 */

import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import { store, retrieve } from '../../utils/storage_helper';
import { clearRoleState } from '../../utils/roles';
import AlertDialog from '../common/AlertDialog';
import '../../styles/organizer.css';
import { forgetAllPending } from '../athlohive/event-detail/pendingPayment';
import { purgeUser } from '../../pages/feed/routeCache';
import { feedUserFrom, resetRouteCacheMemory } from '../../pages/feed/useFeedData';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

// Home, Statistics and Settings are hidden for now — flip `hidden` to bring
// them back. While Home is hidden, /organizer redirects to Events (App.js).
const MENU = [
  { icon: 'bi-house', label: 'Home', to: '/organizer', end: true, hidden: true },
  { icon: 'bi-calendar-event', label: 'Events', to: '/organizer/events' },
  { icon: 'bi-bezier2', label: 'Route builder', to: '/organizer/routes' },
  { icon: 'bi-qr-code-scan', label: 'Participants', to: '/organizer/check-in' },
  { icon: 'bi-trophy', label: 'Results', to: '/organizer/results' },
  { icon: 'bi-bar-chart', label: 'Statistics', to: '/organizer/statistics', hidden: true },
  { icon: 'bi-gear', label: 'Settings', to: '/organizer/settings', hidden: true },
];

function initials(userData) {
  const first = (userData?.first_name || '').trim();
  const last = (userData?.last_name || '').trim();
  if (first || last) return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || 'U';
  const name = (userData?.user_name || userData?.email || 'U').trim();
  return name.slice(0, 2).toUpperCase();
}

/**
 * @param {boolean} narrow       phone width: the rail is a drawer, opened from the top bar
 * @param {boolean} open         the drawer is showing (narrow only)
 * @param {Function} onClose     close the drawer
 */
function OrganizerSidebar({ narrow = false, open = false, onClose = () => {} }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('An error occurred while logging out.');

  const userData = retrieve('userData');
  const displayName = [userData?.first_name, userData?.last_name].filter(Boolean).join(' ')
    || userData?.user_name
    || 'Organizer';

  // Collapsing is a wide-screen choice; on a phone the rail is a full drawer.
  const isCollapsed = collapsed && !narrow;

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      const response = await apiHelper.apiCall('/arcee/user/logout/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        let body = null;
        try { body = await response.json(); } catch (e) { body = null; }
        setErrorMessage(
          (body && (body.error || body.message || body.detail)) ||
          `Logout failed (status ${response.status})`
        );
        setErrorOpen(true);
        setLoggingOut(false);
        return;
      }

      // Same sweep as the feed's sign-out: this is the organizer's exit, but it
      // ends the same session, and an organizer is an athlete too. Their stored
      // routes must not outlive it — a GPS trace shows where someone lives.
      const { userId } = feedUserFrom(retrieve('userData'));
      if (userId !== null && userId !== undefined) purgeUser(userId);
      resetRouteCacheMemory();
      queryClient.clear();

      store('userData', null);

      // A payment noted as unfinished belongs to the person who started it.

      // Every read already checks that, but the row itself would otherwise

      // outlive the session on disk — and an expired one is only ignored,

      // never deleted. Swept here so signing out leaves nothing behind.

      forgetAllPending();

      store('access_token', null);
      clearRoleState();

      // Same signal DashboardHeader sends. NotificationContext resets the bell
      // and reopens its WebSocket on the next session; utils.js forgets its
      // cached login/check answer, so the sign-in page cannot be told this
      // session is still live and bounce the organizer back here.
      window.dispatchEvent(new Event('arcee:auth-change'));

      navigate('/sign-in', { replace: true });
    } catch (err) {
      setErrorMessage(
        err?.type === 'session_expired'
          ? 'Your session has expired. Please login again.'
          : (err?.message || 'Logout failed. Please try again.')
      );
      setErrorOpen(true);
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <aside
      id="org-rail"
      className={`org-rail${isCollapsed ? ' collapsed' : ''}${narrow ? ' is-drawer' : ''}${narrow && open ? ' open' : ''}`}
      aria-label="Organizer menu"
    >
      <div className="org-brand">
        <div className="org-mark">AH</div>
        <div className="org-brandtext">
          <b>{envConfig.appName || 'AthloHive'}</b>
          <span>Organizer</span>
        </div>
        {narrow && (
          <button type="button" className="org-drawerclose" onClick={onClose} aria-label="Close menu">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        )}
      </div>

      <p className="org-lab org-navlbl">Workspace</p>

      {MENU.filter((item) => !item.hidden).map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) => `org-nav${isActive ? ' active' : ''}`}
          title={isCollapsed ? item.label : undefined}
          onClick={narrow ? onClose : undefined}
        >
          <i className={`bi ${item.icon}`} />
          <span className="org-navtext">{item.label}</span>
        </NavLink>
      ))}

      <div className="org-spacer" />
      <div className="org-divider" />

      <div className="org-userchip">
        <div className="org-av">{initials(userData)}</div>
        <div className="org-usertext">
          <b>{displayName}</b>
          <span>Organizer admin</span>
        </div>
      </div>

      <button
        type="button"
        className="org-nav org-logout"
        onClick={handleLogout}
        disabled={loggingOut}
      >
        <i className="bi bi-power" />
        <span className="org-navtext">{loggingOut ? 'Logging out…' : 'Log out'}</span>
      </button>

      {!narrow && (
        <button
          type="button"
          className="org-toggle"
          onClick={() => setCollapsed((x) => !x)}
          aria-label="Toggle sidebar"
        >
          <i className={`bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`} />
        </button>
      )}

      <AlertDialog
        open={errorOpen}
        title="Logout Failed"
        message={errorMessage}
        type="error"
        okLabel="OK"
        showCancel={false}
        onOk={() => setErrorOpen(false)}
        mini_mode={false}
      />
    </aside>
  );
}

export default OrganizerSidebar;
